import diagramEventDriven from '../assets/images/aws-diagram-event-driven.png';
import diagramFintechInfra from '../assets/images/diagram-fintech-infra.drawio.png';
import diagramTrafficHigh from '../assets/images/traffic-control-high-level-architecture.png';
import diagramTrafficLow from '../assets/images/traffic-control-low-level-architecture.png';
import diagramTrafficSeq from '../assets/images/authentication-traffic-shaping-sequence.svg';
import diagramPortfolioCicd from '../assets/images/portfolio-cicd-architecture.drawio.png';
import diagramHubSpoke from '../assets/images/aws-hub-spoke-network-design.png';

export interface Project {
  slug: string;
  title: string;
  category: string;
  description: string;
  highlights: string[];
  technologies: string[];
  diagram?: string;
  diagrams?: { label: string; src: string }[];
}

const projects: Project[] = [
  {
    slug: "event-driven-k8s-job-platform",
    title: "Event-Driven Kubernetes Job Platform",
    category: "AWS · Kubernetes · Serverless",
    description: "Serverless-Kubernetes hybrid for processing high-throughput async workloads at scale. SQS queues feed EventBridge triggers that invoke Lambda — which calculates queue backlog, acquires a distributed DynamoDB lock, and dynamically spawns Kubernetes Jobs on Karpenter-provisioned Spot nodes. Zero standing infrastructure.",
    highlights: [
      "60–70% compute cost reduction by eliminating idle worker infrastructure — jobs spin up on demand and tear down immediately after processing",
      "Distributed DynamoDB locking prevents Lambda thundering-herd race conditions during concurrent scale-out events",
      "Reusable Terraform modules and Helm charts allow onboarding new job types in minutes without duplicating infrastructure",
    ],
    technologies: ["AWS EKS", "Amazon SQS", "AWS Lambda", "EventBridge", "Karpenter", "DynamoDB", "Terraform", "Helm", "IRSA"],
    diagram: diagramEventDriven,
  },
  {
    slug: "multi-env-fintech-infrastructure",
    title: "Multi-Environment Fintech Cloud Infrastructure",
    category: "AWS · IaC · Disaster Recovery · CI/CD",
    description: "End-to-end AWS infrastructure for a payment processing platform spanning 4 environments across 2 regions. Pilot-light DR with Aurora cross-region replica, OIDC-based GitHub Actions CI/CD with zero static credentials, and non-production costs under $60/month across 3 isolated environments using Spot instances and scheduled shutdowns.",
    highlights: [
      "Pilot-light DR across AWS regions with Aurora MySQL read replica — full failover achievable in under 10 minutes",
      "OIDC-based GitHub Actions eliminates all static AWS credentials across every CI/CD pipeline",
      "Non-production infrastructure costs under $60/month across 3 isolated environments through Spot instances and scheduled shutdowns",
    ],
    technologies: ["EC2 ASG", "Aurora MySQL", "ALB", "Terraform", "GitHub Actions", "Packer", "Route53", "ACM", "S3"],
    diagram: diagramFintechInfra,
  },
  {
    slug: "carrier-grade-vpn-traffic-shaping",
    title: "Carrier-Grade VPN Traffic Shaping Platform",
    category: "Linux Networking · VPN · FreeRADIUS · Kafka",
    description: "Per-user bandwidth enforcement engine for a managed VPN service. FreeRADIUS accounting hooks intercept session lifecycle events (Start, Interim-Update, Stop) and drive dynamic Linux tc/qdisc class creation — mapping each user's assigned VPN IP to a unique traffic class via a deterministic classid derived from the IP octets. Aurora persists per-user policy; Kafka decouples the client-connect hook from the shaping logic, keeping the authentication path non-blocking.",
    highlights: [
      "Zero-lookup filter assignment — classid derived deterministically from the last 5 octets of the zero-padded VPN IP, eliminating any runtime database call when creating tc filters",
      "FreeRADIUS Perl module intercepts Acct-Status-Type events to create, update, and destroy per-user HTB qdisc classes in real time, enforcing upload and download rates independently",
      "Kafka decouples client-connect-hook from bandwidth enforcement — VPN connection events published asynchronously, making the authentication critical path non-blocking and independently scalable",
    ],
    technologies: ["WireGuard", "FreeRADIUS", "Linux tc / HTB qdisc", "Kafka", "Redis", "Aurora MySQL", "Perl", "REST API"],
    diagram: diagramTrafficHigh,
    diagrams: [
      { label: "High-Level Architecture", src: diagramTrafficHigh },
      { label: "Low-Level: tc / qdisc Flow", src: diagramTrafficLow },
      { label: "Authentication & Traffic Shaping Sequence", src: diagramTrafficSeq },
    ],
  },
  {
    slug: "aws-hub-spoke-network-design",
    title: "AWS Hub-and-Spoke Network Architecture",
    category: "AWS · Networking · Transit Gateway · Multi-VPC",
    description: "Enterprise-scale multi-VPC network topology built around AWS Transit Gateway as the central hub. On-premises clients connect via Site-to-Site VPN into the TGW, which fans out to isolated spoke VPCs across multiple environments. A dedicated egress VPC centralises all outbound internet traffic through a shared NAT Gateway, and per-environment TGW route tables enforce strict east-west isolation so workloads in dev, staging, and production cannot reach each other without explicit routing policy.",
    highlights: [
      "Centralised egress through a single NAT Gateway VPC — all spoke VPCs route internet-bound traffic via the hub, eliminating per-VPC NAT costs and giving a single choke-point for egress security controls",
      "TGW route table segmentation enforces environment isolation at the network layer — dev, staging, and production spoke VPCs share the same Transit Gateway but cannot communicate without an explicit route table entry",
      "On-premises connectivity via Site-to-Site VPN terminates directly on the Transit Gateway, extending the corporate network into AWS without requiring per-VPC VPN tunnels or a dedicated transit VPC",
    ],
    technologies: ["AWS Transit Gateway", "Site-to-Site VPN", "VPC", "NAT Gateway", "NLB", "Route Tables", "Security Groups", "AWS Shield", "Terraform"],
    diagram: diagramHubSpoke,
  },
  {
    slug: "portfolio-cicd-aws",
    title: "ahmadgharib.com — Portfolio Site",
    category: "AWS · Terraform · GitLab CI/CD · OIDC",
    description: "This site. Static React SPA served globally via CloudFront with a private S3 origin protected by Origin Access Control. Infrastructure provisioned entirely in Terraform with remote state on S3 and DynamoDB locking. A five-stage GitLab CI pipeline lints, tests, builds, and deploys on every push to main — using short-lived OIDC credentials instead of static AWS keys.",
    highlights: [
      "Zero provisioning infrastructure cost — no EC2 instances, no load balancers, no standing infrastructure of any kind. Only pay for S3 storage and CloudFront requests",
      "Zero static AWS credentials — GitLab issues a short-lived OIDC JWT; the deploy stage calls sts:AssumeRoleWithWebIdentity scoped to a single branch and project path",
      "Private S3 bucket served exclusively through CloudFront OAC with SigV4 signing; a CloudFront Function enforces the www→apex 301 redirect at the edge",
    ],
    technologies: ["React 18", "TypeScript", "AWS S3", "CloudFront", "Route 53", "ACM", "IAM OIDC", "Terraform", "GitLab CI"],
    diagram: diagramPortfolioCicd,
  },
];

export default projects;
